import { createPayment } from '@/services/purchase/createPayment';
import { camelizeKeys } from 'humps';
import getPaymentEngineAndProvider from 'utils/getPaymentEngineAndProvider';
import { getPaymentPayload, paymentFailureHandler, paymentPollingHandler } from './payment';
import { requestPayment, updatePurchase } from './purchase';
import { setStripeSubmitting } from './stripe';
import { normalizeStripeResult } from './stripeShared';

/**
 * Builds a Stripe express-wallet payment attempt thunk (Google Pay, Apple Pay, …) using a
 * ConfirmationToken created on the client from the Express Checkout Element.
 *
 * The flow is identical across wallets; only the `payment_type`, the core payments factory, and
 * the engine/provider defaults differ, so they are injected here. It replicates the orchestration
 * contract of the Stripe TDC action — paymentPollingHandler is the single source of truth for
 * terminal outcomes (completed / pending / failed / aborted) — except that the wallet POST is a
 * create-session: the backend creates the PaymentIntent UNCONFIRMED and responds pending with its
 * client_secret, so the response is normalized with `clientConfirmation` context and the
 * `requires_client_confirmation` outcome is returned to the hook, which finalizes with
 * `stripeEngine.confirmPayment` (that call is what dismisses the wallet sheet and runs any 3DS
 * challenge inline).
 *
 * @param {Object} config - Wallet wiring.
 * @param {string} config.paymentType - Stripe payment type (e.g. `google_pay`, `apple_pay`).
 * @param {Object} config.factory - Core payments factory exposing `create({ engine })`.
 * @param {string} config.defaultProvider - Fallback provider when the method has none.
 * @param {string} [config.defaultEngine] - Fallback engine when the method has none. Defaults to
 *   `stripe`.
 * @returns {Function} `(purchaseToken, { confirmationTokenId, walletInfo }) => thunk`.
 */
export function createStripeWalletPaymentAttempt({
  paymentType,
  factory,
  defaultProvider,
  defaultEngine = 'stripe',
}) {
  return function stripeWalletPaymentAttempt(purchaseToken, { confirmationTokenId, walletInfo }) {
    return async (dispatch, getState) => {
      dispatch(updatePurchase(true));
      dispatch(requestPayment());
      dispatch(setStripeSubmitting(true));

      try {
        let resolvePollResult;
        const pollResult = new Promise((resolve) => {
          resolvePollResult = resolve;
        });
        const selectedPaymentMethod = getState().purchase.get('selectedPaymentMethod');
        const {
          engine: paymentEngine,
          provider: paymentProvider,
          extra,
        } = getPaymentEngineAndProvider(selectedPaymentMethod, {
          defaultEngine,
          defaultProvider,
        });
        const walletPayment = factory.create({ engine: paymentEngine });
        const enginePayload = await walletPayment.createPayload(walletInfo, {
          ...extra,
          stripeConfirmationToken: confirmationTokenId,
        });
        const paymentPayload = {
          ...getPaymentPayload(paymentType, paymentEngine),
          payment_provider: paymentProvider,
          ...enginePayload,
        };

        let normalizedResult;

        const response = await createPayment(purchaseToken, paymentPayload, {
          onReceivePayment: (rawResponse) => {
            const normalized = normalizeStripeResult(camelizeKeys(rawResponse), {
              clientConfirmation: true,
            });
            normalizedResult = normalized;

            if (
              normalized.outcome === 'requires_client_confirmation' ||
              normalized.outcome === 'requires_client_action'
            ) {
              resolvePollResult(normalized);
              return normalized;
            }

            // paymentPollingHandler is the single source of truth for
            // completed / pending / failed / aborted (already Stripe-aware).
            paymentPollingHandler(dispatch, purchaseToken, rawResponse);

            // Resolve the awaited result for terminal states; keep polling while pending.
            if (normalized.outcome !== 'pending' || rawResponse.status === 'aborted') {
              resolvePollResult(normalized);
            }

            return normalized;
          },
        });

        if (response?.polls?.payment) {
          return await pollResult;
        }

        return (
          normalizedResult ||
          normalizeStripeResult(camelizeKeys(response), { clientConfirmation: true })
        );
      } catch (error) {
        // Only a genuine Stripe decline reason (e.g. expired_card) should route through the
        // Stripe branch of paymentFailureHandler. HTTP statuses (403/401) and backend messages
        // are NOT decline reasons: coercing them into an errorReason made the Stripe branch win
        // before the code === 403 / message_to_purchaser branches, so e.g. an expired purchase
        // fell through to stripe.default_error instead of expiring. Pass the original error
        // through (preserving code, message_to_purchaser, details, …) and only set errorReason
        // when Stripe actually provided one.
        const errorReason = error?.errorReason;

        paymentFailureHandler(dispatch, {
          ...error,
          paymentEngine: 'stripe',
          paymentType,
          ...(errorReason && { errorReason }),
        });

        return {
          outcome: 'failed',
          errorReason: errorReason || 'default',
          raw: error,
        };
      } finally {
        dispatch(setStripeSubmitting(false));
      }
    };
  };
}

export default createStripeWalletPaymentAttempt;
